// notifyShannonOnSupplementRelease — fires when a deal enters ready_for_supps.
// Two callers: (1) TransitionButton + dispatchActions, when an RM marks the
// build complete (auto-fire on transition into ready_for_supps), and
// (2) ChadFollowupsTab when Chad clicks Notify Shannon on his card after
// writing supp notes. Shannon picks up the deal in her Supplements tab on
// the Finance Board automatically; this notification just makes it impossible
// to miss when it lands.
//
// Mirrors notifyChadOnBuildApproval: in-app notification on /notifications
// (always) + Twilio SMS to Shannon's cell (only if she has a phone on file).
// Fire-and-forget — failures log but never block.
//
// Spec: memory/features/finance-board-redesign.md (Ship 2)
// Pattern: see notifyChadOnBuildApproval.ts (canonical staff-alert pattern).

import { supabase } from "./supabase";
import type { Deal } from "./types";

const PORTAL_BASE_URL = "https://app.bardinroofing.com";
const SHANNON_EMAIL = "sser@bardinroofing.com";

type DealForNotify = Pick<
  Deal,
  "id" | "homeowner_name" | "property_street" | "property_city" | "property_state" | "claim_number" | "chad_supplement_notes"
>;

function toE164(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const digits = raw.replace(/\D/g, "");
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
  return null;
}

function buildAddressLine(deal: DealForNotify): string {
  const parts = [deal.property_street, deal.property_city].filter(Boolean) as string[];
  return parts.join(", ");
}

/**
 * Fire-and-forget. Caller should NOT await unless they need confirmation.
 * Errors are swallowed and logged via console.warn — never bubble up to the UI.
 */
export async function notifyShannonOnSupplementRelease(deal: DealForNotify): Promise<void> {
  try {
    const { data: shannon, error: lookupErr } = await supabase
      .from("team_roster")
      .select("id,full_name,phone")
      .eq("email", SHANNON_EMAIL)
      .eq("is_active", true)
      .maybeSingle();

    if (lookupErr) {
      console.warn("[notifyShannonOnSupplementRelease] team_roster lookup failed:", lookupErr.message);
      return;
    }
    if (!shannon) {
      console.warn("[notifyShannonOnSupplementRelease] Shannon row not found / inactive — skipping text.");
      return;
    }

    const homeowner = deal.homeowner_name || "(no name)";
    const claim = deal.claim_number ? ` (#${deal.claim_number})` : "";
    const addr = buildAddressLine(deal);
    const dealUrl = `${PORTAL_BASE_URL}/finance?view=shannon&tab=supplements`;
    const smsBody = `[Bardin Team] New supps to start: ${homeowner}${claim}${addr ? ` — ${addr}` : ""}. Chad's notes attached. Tap: ${dealUrl}`;

    // 1) In-app notification — appears on Shannon's notification board under "Supplements".
    const inAppPromise = supabase.from("notifications").insert({
      user_id: shannon.id,
      type: "shannon_supplement_release",
      title: `Supplement ready — ${homeowner}`,
      body: deal.chad_supplement_notes || addr || "Tap to start supplements",
      deal_id: deal.id,
      metadata: { url: dealUrl, source: "chad_followups_release" },
    });

    // 2) Bardin Team-line SMS — staff-internal channel, not the homeowner-facing line.
    //    Same Twilio function; tag distinguishes it as a staff alert (not mirrored to RM).
    const phoneE164 = toE164(shannon.phone);
    let smsPromise: Promise<unknown> = Promise.resolve();
    if (phoneE164) {
      smsPromise = supabase.functions.invoke("twilio-send-sms", {
        body: {
          to: phoneE164,
          body: smsBody,
          tag: "shannon_supplement_release",
          // No deal_id passed — prevents twilio-send-sms v16+ from mirroring
          // this onto the deal-owner RM. This is a Shannon-only alert.
        },
      });
    } else {
      console.warn("[notifyShannonOnSupplementRelease] Shannon has no phone on team_roster — skipping SMS.");
    }

    const [inAppRes, smsRes] = await Promise.allSettled([inAppPromise, smsPromise]);
    if (inAppRes.status === "rejected") {
      console.warn("[notifyShannonOnSupplementRelease] in-app insert failed:", inAppRes.reason);
    }
    if (smsRes.status === "rejected") {
      console.warn("[notifyShannonOnSupplementRelease] SMS send failed:", smsRes.reason);
    }
  } catch (err) {
    console.warn("[notifyShannonOnSupplementRelease] unexpected error:", err);
  }
}
